// src/views/AddProducts.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Row,
  Col,
  Button,
  CardTitle,
  Alert,
} from "reactstrap";
import { Link } from "react-router-dom";
import ButtonGroupComponent from "components/Buttons/ButtonGroups";
import ProductForm from "components/Forms/ProductForm";
import AccessoriesForm from "components/Forms/AccessoriesForm";
import NotificationComponent from "../components/Alert/alert";
import PhotoPicker from "components/Uploads/PhotoPicker";
import { useTranslation } from "../hooks/useTranslation";

import { useAddProductMutation } from "../api/api";
import {
  useFetchBrandsQuery,
  useFetchColorsQuery,
  useFetchNumbersQuery,
  useFetchTypesQuery,
} from "../api/cmsPanelsApi";
import { getApiErrorMessage } from "../api/errorUtils";

const list = (maybe) =>
  Array.isArray(maybe?.data) ? maybe.data : Array.isArray(maybe) ? maybe : [];

const toNumberMap = (arr) =>
  Object.fromEntries((Array.isArray(arr) ? arr : []).map((n) => [n.number, 0]));

function AddProducts() {
  const t = useTranslation();
  const notificationComponentRef = useRef();
  const [addProduct, { isLoading }] = useAddProductMutation();

  const [formData, setFormData] = useState({
    code: "",
    name: "",
    brand: 0,
    type: 0,
    color: 0,
    stockPrice: 0,
    importPrice: 0,
    sizes: {},
    productType: "shoes",
    quantity: 0,
  });

  // Image state
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");

  const [activeButton, setActiveButton] = useState("shoes");
  const [missingData, setMissingData] = useState({
    brands: false,
    colors: false,
    types: false,
  });

  const brandRes = useFetchBrandsQuery(activeButton);
  const colorRes = useFetchColorsQuery();
  const typeRes = useFetchTypesQuery(activeButton);
  const numberRes = useFetchNumbersQuery(activeButton);

  const isLoadingData =
    brandRes.isLoading ||
    colorRes.isLoading ||
    typeRes.isLoading ||
    numberRes.isLoading;

  const brandOptions = list(brandRes.data ?? brandRes);
  const colorOptions = list(colorRes.data ?? colorRes);
  const typeOptions = list(typeRes.data ?? typeRes);
  const numberOptions = list(numberRes.data ?? numberRes);

  useEffect(() => {
    if (isLoadingData) return;

    const hasBrands = brandOptions.length > 0;
    const hasColors = activeButton === "accessories" || colorOptions.length > 0;
    const hasTypes = activeButton === "accessories" || typeOptions.length > 0;

    setMissingData({
      brands: !hasBrands,
      colors: activeButton === "accessories" ? false : !hasColors,
      types: activeButton === "accessories" ? false : !hasTypes,
    });

    if (hasBrands && hasColors && hasTypes) {
      setFormData((prev) => ({
        ...prev,
        brand: brandOptions[0]?.id || 0,
        type: activeButton === "accessories" ? 0 : typeOptions[0]?.id || 0,
        color: activeButton === "accessories" ? 0 : colorOptions[0]?.id || 0,
        sizes: activeButton === "accessories" ? {} : toNumberMap(numberOptions),
        productType: activeButton,
      }));
    }

    if (activeButton === "accessories" && hasBrands) {
      setFormData((prev) => ({
        ...prev,
        brand: brandOptions[0]?.id || 0,
        type: 0,
        color: 0,
        sizes: {},
        productType: "accessories",
      }));
    }
  }, [
    activeButton,
    brandOptions,
    colorOptions,
    typeOptions,
    numberOptions,
    isLoadingData,
  ]);

  const fetchDataFromAPIs = (button) => {
    try {
      setActiveButton(button);
      setPhotoFile(null);
      setPhotoPreview("");
    } catch (e) {
      console.error("Error switching product type:", e);
      setActiveButton("shoes");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "code" || name === "name"
          ? value
          : value === "" || value === null
          ? 0
          : parseInt(value, 10),
    }));
  };

  const handleSizeQuantityChange = (e, size) => {
    const qty = parseInt(e.target.value, 10);
    setFormData((prev) => ({
      ...prev,
      sizes: { ...prev.sizes, [size]: Number.isNaN(qty) ? 0 : qty },
    }));
  };

  const validateForm = () => {
    const errors = [];

    if (activeButton === "accessories") {
      // Validate accessories
      if (!formData.code || formData.code.trim() === "") {
        errors.push(t("addProducts.codeRequired") || "Code is required!");
      }
      if (!formData.brand || formData.brand === 0) {
        errors.push(t("addProducts.brandRequired") || "Brand is required!");
      }
      if (!formData.name || formData.name.trim() === "") {
        errors.push(t("addProducts.nameRequired") || "Name is required!");
      }
      if (!formData.stockPrice || formData.stockPrice <= 0) {
        errors.push(
          t("addProducts.stockPriceRequired") ||
            "Stock price must be greater than 0!"
        );
      }
      if (!formData.importPrice || formData.importPrice <= 0) {
        errors.push(
          t("addProducts.importPriceRequired") ||
            "Import price must be greater than 0!"
        );
      }
      if (!formData.quantity || formData.quantity <= 0) {
        errors.push(
          t("addProducts.quantityRequired") ||
            "Quantity must be greater than 0!"
        );
      }
    } else {
      // Validate shoes/textile
      if (!formData.code || formData.code.trim() === "") {
        errors.push(t("addProducts.codeRequired") || "Code is required!");
      }
      if (!formData.brand || formData.brand === 0) {
        errors.push(t("addProducts.brandRequired") || "Brand is required!");
      }
      if (!formData.type || formData.type === 0) {
        errors.push(t("addProducts.typeRequired") || "Type is required!");
      }
      if (!formData.color || formData.color === 0) {
        errors.push(t("addProducts.colorRequired") || "Color is required!");
      }
      if (!formData.stockPrice || formData.stockPrice <= 0) {
        errors.push(
          t("addProducts.stockPriceRequired") ||
            "Stock price must be greater than 0!"
        );
      }
      if (!formData.importPrice || formData.importPrice <= 0) {
        errors.push(
          t("addProducts.importPriceRequired") ||
            "Import price must be greater than 0!"
        );
      }
      // Check if at least one size has quantity > 0
      const hasSizes =
        formData.sizes && Object.values(formData.sizes).some((qty) => qty > 0);
      if (!hasSizes) {
        errors.push(
          t("addProducts.sizesRequired") ||
            "At least one size with quantity > 0 is required!"
        );
      }
    }

    return errors;
  };

  const handleSave = async () => {
    try {
      // Validate form before submitting
      const validationErrors = validateForm();
      if (validationErrors.length > 0) {
        notificationComponentRef.current?.showNotification(
          validationErrors[0],
          "danger"
        );
        return;
      }

      const sizesData = Object.entries(formData?.sizes ?? {}).map(
        ([size, quantity]) => ({ size, quantity: parseInt(quantity, 10) || 0 })
      );

      let payload;

      if (photoFile) {
        const fd = new FormData();
        fd.append("code", String(formData.code));
        fd.append("brand", String(formData.brand));
        fd.append("type", String(formData.type));
        fd.append("color", String(formData.color));
        fd.append("stockPrice", String(formData.stockPrice));
        fd.append("importPrice", String(formData.importPrice));
        fd.append("productType", String(activeButton));

        if (activeButton === "accessories") {
          fd.append("name", String(formData.name || ""));
          fd.append("quantity", String(formData.quantity || 0));
        } else {
          fd.append("sizes", JSON.stringify(sizesData));
        }

        fd.append("photo", photoFile);
        payload = fd;
      } else {
        payload = {
          code: String(formData.code),
          brand: Number(formData.brand),
          type: Number(formData.type),
          color: Number(formData.color),
          stockPrice: Number(formData.stockPrice),
          importPrice: Number(formData.importPrice),
          productType: String(activeButton),
          ...(activeButton === "accessories"
            ? {
                name: String(formData.name || ""),
                quantity: Number(formData.quantity || 0),
              }
            : {
                sizes: sizesData,
              }),
        };
      }

      const result = await addProduct(payload).unwrap();
      if (result && result.success === false) {
        throw new Error(result.message || "Failed to add product");
      }

      // Keep brand and type, reset other fields
      setFormData((prev) => ({
        code: "",
        name: "",
        brand: prev.brand, // Keep the same brand
        type: activeButton === "accessories" ? 0 : prev.type, // Keep the same type (if not accessories)
        color: activeButton === "accessories" ? 0 : colorOptions[0]?.id ?? 0,
        stockPrice: 0,
        importPrice: 0,
        sizes: activeButton === "accessories" ? {} : toNumberMap(numberOptions),
        productType: activeButton,
        quantity: 0,
      }));
      setPhotoFile(null);
      setPhotoPreview("");

      notificationComponentRef.current?.showNotification(
        t("addProducts.productAdded"),
        "success"
      );
    } catch (error) {
      const msg = getApiErrorMessage(
        error,
        t("addProducts.errorAddingProduct"),
        {
          403: "You do not have permission to add products.",
          404: "One of the selected brand, color, type, or location records is no longer available.",
          409: "This product intake conflicts with existing data.",
        }
      );
      console.error(msg);
      notificationComponentRef.current?.showNotification(msg, "danger");
    }
  };

  const isDataLoaded =
    !isLoadingData &&
    (activeButton === "accessories" ||
      (brandOptions.length && colorOptions.length && typeOptions.length));

  return (
    <div className="content">
      <NotificationComponent ref={notificationComponentRef} />

      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <Row>
                <Col className="text-left" sm="6">
                  <h5 className="card-category">{t("addProducts.title")}</h5>
                  <CardTitle tag="h2">{t("products.title")}</CardTitle>
                  <div className="mt-3">
                    <Link to="/admin/catalog/products" className="btn btn-link px-0">
                      Open Catalog Operations
                    </Link>
                  </div>
                </Col>
                <Col sm="6">
                  <ButtonGroupComponent
                    activeButton={activeButton}
                    onButtonClick={fetchDataFromAPIs}
                    buttons={["shoes", "textile", "accessories"]}
                  />
                </Col>
              </Row>
            </CardHeader>

            <CardBody>
              {isLoadingData ? (
                <div className="text-center py-4">{t("common.loading")}</div>
              ) : (
                <>
                  <Alert color="secondary">
                    New CMS products should be created in{" "}
                    <Link to="/admin/catalog/products">Catalog Operations</Link>.
                    This legacy intake remains available for transition-only stock
                    onboarding while migration continues.
                  </Alert>

                  {/* Missing data alerts */}
                  {!isLoadingData && missingData.brands && (
                    <Alert color="danger">
                      {t("addProducts.pleaseAddBrands")}
                      <Link to="/admin/cms"> {t("cms.title")}</Link>.
                    </Alert>
                  )}
                  {!isLoadingData &&
                    missingData.colors &&
                    activeButton !== "accessories" && (
                      <Alert color="danger">
                        {t("addProducts.pleaseAddColors")}
                        <Link to="/admin/cms"> {t("cms.title")}</Link>.
                      </Alert>
                    )}
                  {!isLoadingData &&
                    missingData.types &&
                    activeButton !== "accessories" && (
                      <Alert color="danger">
                        {t("addProducts.pleaseAddTypes")}
                        <Link to="/admin/cms">{t("cms.title")}</Link>.
                      </Alert>
                    )}

                  <Row className="mb-4">
                    <Col md="12">
                      <PhotoPicker
                        label={t("addProducts.uploadPhoto")}
                        file={photoFile}
                        preview={photoPreview}
                        fieldName="photo"
                        notify={(msg, type) =>
                          notificationComponentRef.current?.showNotification(
                            msg,
                            type
                          )
                        }
                        onPick={(file, url) => {
                          setPhotoFile(file);
                          setPhotoPreview(url);
                        }}
                        onClear={() => {
                          setPhotoFile(null);
                          setPhotoPreview("");
                        }}
                      />
                    </Col>
                  </Row>

                  {isDataLoaded && activeButton !== "accessories" && (
                    <ProductForm
                      formData={formData}
                      brandOptions={brandOptions}
                      colorOptions={colorOptions}
                      typeOptions={typeOptions}
                      handleChange={handleChange}
                      numberOptions={toNumberMap(numberOptions)}
                      handleSizeQuantityChange={handleSizeQuantityChange}
                      productType={activeButton}
                      disabled={
                        missingData.brands ||
                        missingData.colors ||
                        missingData.types
                      }
                      loading={isLoading}
                    />
                  )}

                  {isDataLoaded && activeButton === "accessories" && (
                    <AccessoriesForm
                      formData={formData}
                      brandOptions={brandOptions}
                      handleChange={handleChange}
                      disabled={missingData.brands}
                      loading={isLoading}
                    />
                  )}
                </>
              )}
            </CardBody>

            <CardFooter style={{ textAlign: "right" }}>
              <Button
                className="btn-fill"
                color="primary"
                onClick={handleSave}
                disabled={
                  missingData.brands ||
                  (activeButton !== "accessories" &&
                    (missingData.colors || missingData.types)) ||
                  isLoading
                }
              >
                {isLoading
                  ? t("common.saving") || "Saving..."
                  : t("common.save")}
              </Button>
            </CardFooter>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default AddProducts;
